import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, Stethoscope, FileCheck2, Wallet, MessageSquare, Database } from "lucide-react";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import HeroFields from "@/components/HeroFields";

/**
 * Sukham — in-house venture. Every external integration is simulated in the
 * current prototype. Terminology: "patient context model", never "digital twin".
 */
const workflow = [
  { icon: FileCheck2, title: "ABHA intake (simulated)", body: "Scan-and-share style intake collects patient context before the visit begins." },
  { icon: Stethoscope, title: "Queue → clinician workspace", body: "One thread carries context, deltas since the last visit and missing-information flags into the consultation." },
  { icon: FileCheck2, title: "Bilingual e-prescription", body: "A draft is generated for the clinician to review; nothing leaves the room without approval." },
  { icon: Wallet, title: "GST + UPI billing (simulated)", body: "Billing and receipts come from the same visit thread, not a separate ledger." },
  { icon: MessageSquare, title: "WhatsApp reminders (simulated)", body: "Follow-ups are queued from the visit record with explicit patient consent." },
  { icon: Database, title: "Consented patient record", body: "The record is written back to a consented store, owned by the patient, visible to the clinic owner." },
];

const trust = [
  "Provenance on every fact, summary and inference",
  "Uncertainty is shown to the clinician, not hidden",
  "Consent gates every external integration",
  "Role-based access for owner, clinician and front desk",
  "Audit trail on every write",
  "Clear separation of facts, summaries and inferences",
];

const evidence = [
  "Time from arrival to prescription",
  "Corrections applied by the clinician to drafts",
  "Draft acceptance rate",
  "Offline recovery after a network drop",
  "Subgroup performance before any benefit is published",
];

const Sukham = () => (
  <PageShell
    title="Sukham · An in-house venture | Skandor Technologies"
    description="Sukham is an in-house Skandor product for small OPD clinics in India: simulated intake, bilingual e-prescription, billing, reminders and a consented patient record."
    canonicalPath="/work/sukham"
  >
    <section className="relative overflow-hidden bg-background py-16 md:py-24">
      <HeroFields compact />
      <div className="container relative z-10 mx-auto">
        <Link to="/work" className="mb-8 inline-flex min-h-11 items-center gap-2 text-sm text-ink-3 hover:text-steel-2">
          <ArrowLeft className="h-4 w-4" /> All work
        </Link>
        <div className="max-w-4xl">
          <p className="eyebrow">Venture 01 · In-house · In development</p>
          <h1 className="mt-4">Sukham — one calm operating flow for the everyday clinic.</h1>
          <p className="lede mt-5">
            A clickable prototype for small Indian OPD clinics. It connects simulated ABHA-based intake,
            consultation, bilingual e-prescriptions, GST and UPI billing, WhatsApp reminders, a consented
            patient record and clinic-owner visibility. A source-linked patient context model is the next phase.
          </p>
          <div className="my-7 flex flex-wrap gap-2">
            {["Interactive prototype", "Simulated integrations", "AI context layer in R&D"].map((t) => (
              <span key={t} className="rounded-md border border-line bg-tint px-3 py-1 text-xs text-ink-2">{t}</span>
            ))}
          </div>
          <Button asChild><a href="#story">View the product story <ArrowRight /></a></Button>
        </div>
      </div>
    </section>

    <section id="story" className="section-tint py-16 md:py-24">
      <div className="container mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
        <div>
          <p className="eyebrow">The problem</p>
          <h2 className="mt-4 text-2xl">Departmental handoffs, not one visit</h2>
          <p className="mt-3 text-ink-2">
            Registration, consultation, prescribing, billing and follow-up run on different books, apps and
            staff. Context is lost between them.
          </p>
        </div>
        <div>
          <p className="eyebrow">The thesis</p>
          <h2 className="mt-4 text-2xl">One visit, one thread</h2>
          <p className="mt-3 text-ink-2">
            A visit should move as one thread. The clinician stays with the patient, and the owner sees the
            whole day at a glance.
          </p>
        </div>
      </div>
    </section>

    <section className="bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-5xl">
        <p className="eyebrow">The workflow</p>
        <h2 className="mt-4">Intake → consultation → prescription → payment → consented record.</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2">
          {workflow.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={s.title} className="surface-card p-6">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-steel-2">0{i + 1}</span>
                  <Icon className="h-4 w-4 text-teal" aria-hidden="true" />
                </div>
                <h3 className="mt-3">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-2">{s.body}</p>
              </li>
            );
          })}
        </ol>
        <p className="mt-6 font-mono text-[.68rem] text-ink-3">
          All external integrations shown above are simulated in the current prototype.
        </p>
      </div>
    </section>

    <section className="section-tint py-16 md:py-24">
      <div className="container mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
        <div>
          <p className="eyebrow">The AI direction</p>
          <h2 className="mt-4 text-2xl">A source-linked patient context model</h2>
          <ul className="mt-4 space-y-2 text-ink-2">
            {["Source-linked pre-visit briefs", "Changes since the previous visit", "Missing-information flags", "Routine-documentation drafts"].map((x) => (
              <li key={x}>· {x}</li>
            ))}
          </ul>
          <p className="mt-4 text-sm italic text-ink-3">
            We say &quot;patient context model&quot;, not &quot;digital twin&quot;, because the prototype is a
            simulated workflow rather than a predictive patient representation.
          </p>
        </div>
        <div>
          <p className="eyebrow">Human control</p>
          <h2 className="mt-4 text-2xl">The clinician approves every clinical output</h2>
          <p className="mt-3 text-ink-2">
            Drafts assist; they do not decide. Every prescription, summary and downstream instruction is
            reviewed before it leaves the room.
          </p>
        </div>
      </div>
    </section>

    <section className="bg-background py-16 md:py-24">
      <div className="container mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
        <div>
          <p className="eyebrow">Trust</p>
          <h2 className="mt-4 inline-flex items-center gap-2 text-2xl">
            <ShieldCheck className="h-5 w-5 text-teal" aria-hidden="true" /> Safety by construction
          </h2>
          <ul className="mt-4 space-y-2 text-sm text-ink-2">{trust.map((t) => <li key={t}>· {t}</li>)}</ul>
        </div>
        <div>
          <p className="eyebrow">Evidence</p>
          <h2 className="mt-4 text-2xl">Measured before benefits are published</h2>
          <ul className="mt-4 space-y-2 text-sm text-ink-2">{evidence.map((t) => <li key={t}>· {t}</li>)}</ul>
        </div>
      </div>
      <div className="container mx-auto mt-14 max-w-3xl border-t border-line pt-10 text-center">
        <h2>Building something similar in a regulated field?</h2>
        <p className="mt-4 text-ink-2">
          Bring us the problem that crosses product, AI and operations. We start with a diagnosis.
        </p>
        <Button asChild className="mt-6">
          <a href="/#contact">Talk to a senior partner <ArrowRight /></a>
        </Button>
      </div>
    </section>
  </PageShell>
);

export default Sukham;
